import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import AuroraBackground from '@/components/motion/AuroraBackground';
import FooterWordmark from './FooterWordmark';
import { GUIDES, guidePath } from '@/seo/content/guides';
import { SEO_UI } from '@/seo/content/ui';
import { AGENCY } from '@/seo/site';
import { useSeoLang } from '@/seo/useSeoLang';
import '@/styles/home.css';

const linkClass = 'inline-flex items-center min-h-[44px] text-[#bfe3ea] hover:text-glow text-sm transition-colors';
const legalClass = 'inline-flex items-center min-h-[44px] text-[#9cc6cf] hover:text-glow transition-colors';
const socialClass = 'w-11 h-11 rounded-full border border-white/15 grid place-items-center text-[#d8f3f7] hover:text-ink hover:bg-glow hover:border-glow transition-all duration-300';

export default function Footer() {
  const { t } = useTranslation();
  const lang = useSeoLang();
  const { containerRef } = useScrollReveal({ threshold: 0.05, staggerDelay: 100 });

  return (
    <footer className="site-footer relative pt-8 md:pt-10 pb-8 section-gradient-footer overflow-hidden text-[#d8f3f7]">
      <AuroraBackground variant="dark" className="footer-aurora" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-glow/60 to-transparent" aria-hidden="true" />
      <div ref={containerRef} className="max-w-7xl mx-auto px-4 md:px-6 relative z-10">
        <FooterWordmark />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1fr] gap-10 md:gap-12 mb-12">
          <div className="reveal">
            <h3 className="footer-brand text-white mb-3">
              {t('footer.company')}
            </h3>
            <p className="text-[#bfe3ea] text-sm leading-relaxed max-w-xs">
              {t('footer.tagline')}
            </p>
            <div className="flex gap-2.5 mt-6">
              <a href={AGENCY.instagram} target="_blank" rel="noopener noreferrer me" className={socialClass} aria-label="Instagram @aquamarina_ll">
                <i className="ri-instagram-line text-lg" aria-hidden="true" />
              </a>
              <a href={AGENCY.whatsapp} target="_blank" rel="noopener noreferrer" className={socialClass} aria-label="WhatsApp">
                <i className="ri-whatsapp-line text-lg" aria-hidden="true" />
              </a>
            </div>
          </div>

          <div className="reveal">
            <h4 className="footer-head">
              {t('homeUi.footer.links')}
            </h4>
            <ul className="-my-2.5">
              <li><Link to="/" className={linkClass}>{t('footer.links.home')}</Link></li>
              <li><Link to="/sobre-nos" className={linkClass}>{t('nav.about')}</Link></li>
              <li><Link to="/escunas" className={linkClass}>{t('footer.links.tours')}</Link></li>
              <li><Link to="/contato" className={linkClass}>{t('footer.links.contact')}</Link></li>
            </ul>
          </div>

          <div className="reveal">
            <h4 className="footer-head">
              <Link to="/guia" className="hover:text-glow transition-colors">{SEO_UI[lang].guides}</Link>
            </h4>
            <ul className="-my-2.5">
              {GUIDES.map((guide) => (
                <li key={guide.id}>
                  <Link to={guidePath(guide, lang)} className={linkClass}>{guide.copy[lang].label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="reveal">
            <h4 className="footer-head">
              {t('footer.links.contact')}
            </h4>
            <ul className="space-y-3 text-sm text-[#bfe3ea]">
              <li className="flex items-start gap-2.5">
                <i className="ri-mail-line text-glow mt-0.5" aria-hidden="true" />
                <span className="break-all">{t('contact.email')}</span>
              </li>
              <li className="flex items-center gap-2.5">
                <i className="ri-phone-line text-glow" aria-hidden="true" />
                <span className="type-num">+55 11 95245-1307</span>
              </li>
              <li className="flex items-start gap-2.5">
                <i className="ri-map-pin-line text-glow mt-0.5" aria-hidden="true" />
                <span>{t('contact.address')}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-[#9cc6cf]">
            &copy; {new Date().getFullYear()} {t('footer.company')}. {t('footer.rights')}
          </p>
          <div className="flex flex-wrap justify-center gap-x-5 text-xs">
            <Link to="/politica-de-privacidade" className={legalClass}>{t('homeUi.footer.privacy')}</Link>
            <Link to="/politica-de-cookies" className={legalClass}>{t('homeUi.footer.cookies')}</Link>
            <Link to="/termos-e-condicoes" className={legalClass}>{t('homeUi.footer.terms')}</Link>
            <Link to="/politica-de-reservas" className={legalClass}>{t('homeUi.footer.reservations')}</Link>
            <Link to="/politica-de-cancelamentos" className={legalClass}>{t('homeUi.footer.cancellations')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
